import { useEffect, useRef, useState } from 'react';
import type { StoryElement } from '../types';
import { storyPermalinkUrl } from '../utils/storyPermalink';
import { IconLink } from './icons';

type CopyState = 'idle' | 'copied' | 'failed';

export function StoryPermalinkButton({
  story,
}: {
  story: Pick<StoryElement, 'id' | 'topic_id'>;
}) {
  const [copyState, setCopyState] = useState<CopyState>('idle');
  const resetTimer = useRef<number | null>(null);

  useEffect(() => () => {
    if (resetTimer.current !== null) window.clearTimeout(resetTimer.current);
  }, []);

  const handleCopy = async (event: React.MouseEvent) => {
    event.stopPropagation();
    try {
      await navigator.clipboard.writeText(storyPermalinkUrl(story));
      setCopyState('copied');
    } catch {
      setCopyState('failed');
    }
    if (resetTimer.current !== null) window.clearTimeout(resetTimer.current);
    resetTimer.current = window.setTimeout(() => setCopyState('idle'), 2500);
  };

  const message = copyState === 'copied'
    ? 'Story link copied'
    : copyState === 'failed' ? 'Could not copy story link' : '';

  return (
    <span className="story-permalink-control">
      <button
        className={`feed-item-action-btn${copyState === 'copied' ? ' active' : ''}`}
        onClick={handleCopy}
        title={copyState === 'copied' ? 'Story link copied' : 'Copy story link'}
        aria-label={copyState === 'copied' ? 'Story link copied' : 'Copy story link'}
      >
        <IconLink />
      </button>
      {message && (
        <span className={`story-permalink-message${copyState === 'failed' ? ' error' : ''}`} role="status">
          {message}
        </span>
      )}
    </span>
  );
}
